/**
 * The shot controls (UI-001): aim readout and fine adjust, power, spin, shoot.
 *
 * Aiming itself is a drag on the CLOTH, not in here — a pool player points at where
 * they want the ball to go. These are the adjustments around that: a quarter-degree
 * nudge either way, how hard, and where on the cue ball.
 *
 * The panel owns power and spin as its own state and hands them over on request. It
 * owns no game rules and decides nothing about shots; UI-002's controller does that.
 */
import { stepPower, wheelNotches } from './power-wheel.ts';
import { createHoldRepeat } from './hold-repeat.ts';
import type { TipOffset } from '../game/spin.ts';
import { createSpinPad, type SpinPad } from './spin-pad.ts';

export interface ControlHandlers {
  onNudge(towards: 'left' | 'right'): void;
  onShoot(): void;
  /** Power changed, 0–1, by slider or wheel (UI-015: the cue shows it). */
  onPower?(power: number): void;
  /** Spin changed, as the spin ball shows it (UI-016: the cue shows side spin). */
  onSpin?(spin: TipOffset): void;
}

export interface Controls {
  /**
   * Update the aim readout with the bearing the player sees — degrees clockwise from
   * straight up the screen. The viewport computes it, because only the viewport knows
   * which way round the table is drawn. `undefined` while there is nothing to aim.
   */
  setAim(bearingDegrees: number | undefined): void;
  /** Lock the controls while a shot is in flight. */
  setEnabled(enabled: boolean): void;
  power(): number;
  spin(): TipOffset;
  resetSpin(): void;
  destroy(): void;
}

/**
 * A shot below this is a tap that barely reaches the next ball; the slider starts here
 * rather than at zero so every position on it is a shot worth taking.
 */
const MIN_POWER_PERCENT = 5;
const DEFAULT_POWER_PERCENT = 50;

export function createControls(
  host: HTMLElement,
  maxTipOffsetR: number,
  handlers: ControlHandlers,
): Controls {
  host.innerHTML = `
    <div class="controls__aim">
      <button type="button" class="btn btn--nudge" data-nudge="left"
              aria-label="Aim a tenth of a degree left">&lsaquo;</button>
      <p class="controls__readout">
        <span class="controls__label">Aim</span>
        <span class="controls__value numeral" data-aim>&mdash;</span>
      </p>
      <button type="button" class="btn btn--nudge" data-nudge="right"
              aria-label="Aim a tenth of a degree right">&rsaquo;</button>
    </div>

    <label class="controls__power">
      <span class="controls__label">
        Power <span class="controls__value numeral" data-power-value></span>
      </span>
      <input class="controls__slider" type="range" data-power
             min="${String(MIN_POWER_PERCENT)}" max="100" step="1"
             value="${String(DEFAULT_POWER_PERCENT)}" />
    </label>

    <div class="controls__actions">
      <div class="spin" data-spin></div>
      <button type="button" class="btn btn--primary" data-shoot>Shoot</button>
    </div>
  `;

  const aimValue = need<HTMLSpanElement>(host, '[data-aim]');
  const powerInput = need<HTMLInputElement>(host, '[data-power]');
  const powerValue = need<HTMLSpanElement>(host, '[data-power-value]');
  const shootButton = need<HTMLButtonElement>(host, '[data-shoot]');
  const spinHost = need<HTMLDivElement>(host, '[data-spin]');
  const nudgeButtons = [...host.querySelectorAll<HTMLButtonElement>('[data-nudge]')];

  let spin: TipOffset = { spinX: 0, spinY: 0 };
  /*
   * UI-009: always there, beside Shoot, with no button to open it (owner, 2026-09-25).
   * It used to be built on first open to save a few elements; not worth a tap.
   */
  const spinPad: SpinPad = createSpinPad(spinHost, maxTipOffsetR, (next) => {
    spin = next;
    handlers.onSpin?.(next);
  });

  /**
   * Show the power, and tell the page it changed. Not while the controls are still being
   * built: the page is not ready to be told, and sets its own first look once they exist.
   */
  function renderPower(notify = true): void {
    powerValue.textContent = `${powerInput.value}%`;
    if (notify) handlers.onPower?.(Number(powerInput.value) / 100);
  }

  const onPower = (): void => {
    renderPower();
  };

  const onShoot = (): void => {
    handlers.onShoot();
  };

  /*
   * UI-007: a press steps once; holding keeps stepping, faster the longer it is held
   * (hold-repeat.ts). Pointer events drive the hold; a keyboard press still arrives as
   * a click with no pointer behind it (`detail === 0`), and steps once.
   */
  const timers = {
    setTimeout: (run: () => void, ms: number) => window.setTimeout(run, ms),
    clearTimeout: (handle: number) => {
      window.clearTimeout(handle);
    },
  };
  const holds = nudgeButtons.map((button) => {
    const towards = button.dataset['nudge'] === 'left' ? 'left' : 'right';
    const hold = createHoldRepeat(() => {
      if (!button.disabled) handlers.onNudge(towards);
    }, timers);
    const press = (event: PointerEvent): void => {
      if (button.disabled || event.button !== 0) return;
      // Keep the press on this button even if the finger drifts, and stop the page
      // selecting text or opening a long-press menu while it is held.
      event.preventDefault();
      button.setPointerCapture(event.pointerId);
      hold.start();
    };
    const release = (): void => {
      hold.stop();
    };
    const keyboard = (event: MouseEvent): void => {
      if (event.detail === 0 && !button.disabled) handlers.onNudge(towards);
    };
    const noMenu = (event: Event): void => {
      event.preventDefault();
    };
    button.addEventListener('pointerdown', press);
    for (const type of ['pointerup', 'pointercancel', 'lostpointercapture'] as const) {
      button.addEventListener(type, release);
    }
    button.addEventListener('click', keyboard);
    button.addEventListener('contextmenu', noMenu);
    return {
      hold,
      remove() {
        hold.stop();
        button.removeEventListener('pointerdown', press);
        for (const type of ['pointerup', 'pointercancel', 'lostpointercapture'] as const) {
          button.removeEventListener(type, release);
        }
        button.removeEventListener('click', keyboard);
        button.removeEventListener('contextmenu', noMenu);
      },
    };
  });

  powerInput.addEventListener('input', onPower);

  /*
   * UI-014: the mouse wheel sets power anywhere on the game, not only over the slider.
   * Not while a popup is open (it may scroll), not while shooting (the slider is off),
   * and never with Ctrl held, which is how a trackpad pinch-zooms.
   */
  let wheelCarry = 0;
  const onWheel = (event: WheelEvent): void => {
    if (powerInput.disabled || event.ctrlKey || document.querySelector('dialog[open]') !== null) {
      return;
    }
    event.preventDefault();
    const step = stepPower(
      Number(powerInput.value),
      wheelCarry,
      wheelNotches(event.deltaY, event.deltaMode),
      MIN_POWER_PERCENT,
      100,
    );
    wheelCarry = step.carry;
    if (String(step.percent) !== powerInput.value) {
      powerInput.value = String(step.percent);
      renderPower();
    }
  };
  window.addEventListener('wheel', onWheel, { passive: false });
  shootButton.addEventListener('click', onShoot);

  renderPower(false);

  return {
    setAim(bearingDegrees) {
      aimValue.textContent = bearingDegrees === undefined ? '—' : `${bearingDegrees.toFixed(1)}°`;
    },

    setEnabled(enabled) {
      shootButton.disabled = !enabled;
      powerInput.disabled = !enabled;
      for (const button of nudgeButtons) button.disabled = !enabled;
      // A shot taken mid-hold must not keep turning the cue under the animation.
      if (!enabled) for (const { hold } of holds) hold.stop();
      spinPad.setEnabled(enabled);
    },

    power: () => Number(powerInput.value) / 100,
    spin: () => spin,

    resetSpin() {
      spin = { spinX: 0, spinY: 0 };
      spinPad.reset();
      handlers.onSpin?.(spin);
    },

    destroy() {
      powerInput.removeEventListener('input', onPower);
      window.removeEventListener('wheel', onWheel);
      shootButton.removeEventListener('click', onShoot);
      for (const entry of holds) entry.remove();
      spinPad.destroy();
    },
  };
}

function need<T extends Element>(root: ParentNode, selector: string): T {
  const found = root.querySelector<T>(selector);
  if (found === null) throw new Error(`controls: ${selector} is missing`);
  return found;
}
